import { ChevronLeft, ChevronRight, Download, Minus, Plus, RotateCw } from "lucide-react";
import { useState } from "react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import type { KycDocument } from "@/types/kyc";

interface DocumentViewerModalProps {
  documents: KycDocument[];
  initialDoc: KycDocument | null;
  open: boolean;
  onClose: () => void;
  onDownload: (doc: KycDocument) => void;
}

function resolveDocUrl(url?: string): string {
  if (!url) return "";
  if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `http://localhost:5001${url.startsWith("/") ? "" : "/"}${url}`;
}

function isPdf(doc?: KycDocument | null): boolean {
  if (!doc) return false;
  const fileName = (doc.fileName || (doc as any).name || "").toLowerCase();
  const url = (doc.previewUrl || (doc as any).url || "").toLowerCase();
  return fileName.endsWith(".pdf") || url.includes(".pdf") || url.startsWith("data:application/pdf");
}

export default function DocumentViewerModal({
  documents,
  initialDoc,
  open,
  onClose,
  onDownload,
}: DocumentViewerModalProps) {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  const active = documents[index] ?? initialDoc;
  const activeUrl = active ? resolveDocUrl(active.previewUrl || (active as any).url) : "";
  const isPdfDoc = active ? isPdf(active) : false;

  function handleClose() {
    setZoom(100);
    setRotation(0);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={active?.docType || (active as any)?.type || "Document"}
      size="lg"
      footer={
        <div className="flex items-center justify-end border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </div>
      }
    >
      {active && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[13px] text-ink-secondary">
              {active.fileName || (active as any).name} · {active.fileSize || (active as any).size}
            </p>
            {activeUrl && (
              <a
                href={activeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-primary-500 hover:underline"
              >
                Open Original in New Tab
              </a>
            )}
          </div>

          <div className="relative flex h-[480px] items-center justify-center overflow-hidden rounded-lg bg-[#0F172A]">
            {isPdfDoc ? (
              <div className="flex flex-col items-center justify-center gap-3 p-6 text-center text-white">
                <p className="text-base font-semibold">PDF Document Preview</p>
                <p className="text-xs text-slate-400 max-w-sm">
                  {active.fileName || (active as any).name}
                </p>
                {activeUrl ? (
                  <iframe
                    src={activeUrl}
                    title={active.fileName}
                    className="h-[360px] w-full rounded border-0 bg-white"
                  />
                ) : (
                  <p className="text-xs text-slate-400">PDF file stream not available</p>
                )}
              </div>
            ) : activeUrl ? (
              <img
                src={activeUrl}
                alt={active.docType || "Document"}
                className="max-h-full max-w-full object-contain transition-transform"
                style={{ transform: `scale(${zoom / 100}) rotate(${rotation}deg)` }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <div className="p-6 text-center text-slate-400 text-sm">
                No preview available for this document
              </div>
            )}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
              <div className="flex items-center gap-1 rounded-full bg-white px-2 py-1 shadow-dropdown">
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.max(50, z - 25))}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                  aria-label="Zoom out"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-12 text-center text-xs font-medium text-ink">{zoom}%</span>
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.min(200, z + 25))}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                  aria-label="Zoom in"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <span className="mx-1 h-4 w-px bg-slate-200" />
                <button
                  type="button"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                  aria-label="Rotate"
                >
                  <RotateCw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onDownload(active)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                  aria-label="Download"
                >
                  <Download className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {documents.length > 1 && (
            <div>
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <button
                  type="button"
                  onClick={() => setIndex((i) => Math.max(0, i - 1))}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-300 text-ink-muted hover:bg-slate-50 cursor-pointer"
                  aria-label="Previous document"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {documents.map((d, i) => (
                  <button
                    key={d.docType}
                    type="button"
                    onClick={() => setIndex(i)}
                    className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 transition-colors cursor-pointer ${
                      i === index ? "border-[#1E40AF]" : "border-slate-200 hover:border-slate-300"
                    }`}
                    aria-label={`View ${d.docType}`}
                  >
                    <img src={d.previewUrl} alt={d.docType} className="h-full w-full object-cover" />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setIndex((i) => Math.min(documents.length - 1, i + 1))}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-300 text-ink-muted hover:bg-slate-50 cursor-pointer"
                  aria-label="Next document"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-xs text-ink-muted">
                {index + 1} of {documents.length} · {active.docType}
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}